
if (!customElements.get('dropdown-advanced')) {
  customElements.define(
    'dropdown-advanced',
    class DropdownAdvanced extends HTMLElement {
      connectedCallback() {
        if (this.dataset.upgraded !== undefined) return;

        this.select = this.querySelector('.dropdown-adv__native');
        this.trigger = this.querySelector('.dropdown-adv__trigger');
        this.panel = this.querySelector('.dropdown-adv__panel');
        this.list = this.querySelector('.dropdown-adv__list');
        this.valueEl = this.querySelector('[data-dropdown-value]');

        if (!this.select || !this.trigger || !this.panel || !this.list || !this.valueEl) return;

        this.activeIndex = -1;
        this.search = null;
        this.controller = new AbortController();

        const signal = this.controller.signal;

        this.rebuild();

        this.select.setAttribute('tabindex', '-1');
        this.select.setAttribute('aria-hidden', 'true');
        this.trigger.hidden = false;
        this.dataset.upgraded = '';

        this.trigger.addEventListener('click', this.onTriggerClick.bind(this), { signal: signal });
        this.trigger.addEventListener('keydown', this.onKeydown.bind(this), { signal: signal });
        this.list.addEventListener('click', this.onOptionClick.bind(this), { signal: signal });

        this.list.addEventListener(
          'mousedown',
          function (event) {
            event.preventDefault();
          },
          { signal: signal }
        );
        this.addEventListener('focusout', this.onFocusOut.bind(this), { signal: signal });
        document.addEventListener('click', this.onDocumentClick.bind(this), { signal: signal });

        this.observer = new MutationObserver(this.rebuild.bind(this));
        this.observer.observe(this.select, { childList: true });
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
        if (this.observer) this.observer.disconnect();
        delete this.dataset.upgraded;
      }

      get options() {
        return Array.from(this.list.querySelectorAll('.dropdown-adv__option'));
      }

      get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      rebuild() {
        const base = this.select.id || 'dropdown';
        const previousFilter = this.search ? this.search.value : '';

        this.list.textContent = '';

        Array.from(this.select.options).forEach(function (option, index) {
          const item = document.createElement('li');
          item.className = 'dropdown-adv__option';
          item.id = base + '-opt-' + index;
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
          item.dataset.value = option.value;
          if (option.disabled) item.dataset.disabled = '';
          item.textContent = option.textContent.trim();
          this.list.appendChild(item);
        }, this);

        const selected = this.select.selectedOptions[0];
        this.valueEl.textContent = selected ? selected.textContent.trim() : '';

        const threshold = Number(this.dataset.searchableFrom || 9);
        if (this.select.options.length > threshold) {
          this.mountSearch();
          if (previousFilter) {
            this.search.value = previousFilter;
            this.filter(previousFilter);
          }
        }
      }

      mountSearch() {
        if (this.search) return;

        const base = this.select.id || 'dropdown';

        this.search = document.createElement('input');
        this.search.type = 'search';
        this.search.className = 'dropdown-adv__search';
        this.search.id = base + '-search';
        this.search.setAttribute('role', 'combobox');
        this.search.setAttribute('aria-expanded', 'true');
        this.search.setAttribute('aria-controls', this.list.id);
        this.search.setAttribute('aria-autocomplete', 'list');
        this.search.setAttribute('autocomplete', 'off');
        const placeholder = this.dataset.searchPlaceholder || 'Rechercher';
        this.search.placeholder = placeholder;
        this.search.setAttribute('aria-label', placeholder);

        this.results = document.createElement('p');
        this.results.className = 'visually-hidden';
        this.results.id = base + '-results';
        this.results.setAttribute('aria-live', 'polite');

        this.panel.insertBefore(this.results, this.list);
        this.panel.insertBefore(this.search, this.results);

        const signal = this.controller.signal;
        this.search.addEventListener('input', this.onSearchInput.bind(this), { signal: signal });
        this.search.addEventListener('keydown', this.onKeydown.bind(this), { signal: signal });

        this.trigger.removeAttribute('role');
        this.trigger.removeAttribute('aria-controls');
        this.trigger.removeAttribute('aria-activedescendant');
      }

      get commander() {
        return this.search || this.trigger;
      }

      open() {
        if (this.isOpen) return;

        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');

        const current = this.visibleOptions().findIndex(function (item) {
          return item.getAttribute('aria-selected') === 'true';
        });
        this.setActive(current < 0 ? 0 : current);

        if (this.search) this.search.focus();
      }

      close(options) {
        if (!this.isOpen) return;

        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');

        if (this.search) {
          this.search.value = '';
          this.filter('');
        }

        this.commander.removeAttribute('aria-activedescendant');
        this.options.forEach(function (item) {
          delete item.dataset.active;
        });
        this.activeIndex = -1;

        if (options && options.focus) this.trigger.focus();
      }

      toggle() {
        if (this.isOpen) this.close({ focus: true });
        else this.open();
      }

      commit(value) {
        this.select.value = value;

        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));

        this.options.forEach(function (item) {
          item.setAttribute('aria-selected', item.dataset.value === value ? 'true' : 'false');
        });

        const selected = this.select.selectedOptions[0];
        this.valueEl.textContent = selected ? selected.textContent.trim() : '';

        this.close({ focus: true });
      }

      visibleOptions() {
        return this.options.filter(function (item) {
          return item.hidden !== true && item.dataset.disabled === undefined;
        });
      }

      setActive(index) {
        const visible = this.visibleOptions();
        if (!visible.length) {
          this.activeIndex = -1;
          this.commander.removeAttribute('aria-activedescendant');
          return;
        }

        const bounded = Math.max(0, Math.min(index, visible.length - 1));

        this.options.forEach(function (item) {
          delete item.dataset.active;
        });

        const item = visible[bounded];
        item.dataset.active = '';
        this.activeIndex = bounded;
        this.commander.setAttribute('aria-activedescendant', item.id);
        item.scrollIntoView({ block: 'nearest' });
      }

      moveActive(step) {
        this.setActive(this.activeIndex + step);
      }

      onTriggerClick(event) {
        event.preventDefault();
        this.toggle();
      }

      onOptionClick(event) {
        const item = event.target.closest('.dropdown-adv__option');
        if (!item || item.dataset.disabled !== undefined) return;
        this.commit(item.dataset.value);
      }

      onSearchInput() {
        this.filter(this.search.value);
      }

      filter(term) {
        const needle = term.trim().toLowerCase();

        this.options.forEach(function (item) {
          item.hidden = needle !== '' && item.textContent.toLowerCase().indexOf(needle) === -1;
        });

        const count = this.visibleOptions().length;

        if (this.results) {
          this.results.textContent =
            count === 0 ? this.emptyText() : count + ' résultat' + (count > 1 ? 's' : '');
        }

        this.renderEmptyState(count === 0);
        this.setActive(0);
      }

      emptyText() {
        return this.dataset.emptyText || 'Aucun résultat';
      }

      renderEmptyState(empty) {
        let note = this.panel.querySelector('.dropdown-adv__empty');
        if (!empty) {
          if (note) note.remove();
          return;
        }
        if (note) return;
        note = document.createElement('p');
        note.className = 'dropdown-adv__empty';
        note.textContent = this.emptyText();
        this.panel.appendChild(note);
      }

      onKeydown(event) {
        const key = event.key;

        if (!this.isOpen) {
          if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
            event.preventDefault();
            this.open();
          }
          return;
        }

        if (key === 'Escape') {
          event.preventDefault();
          this.close({ focus: true });
          return;
        }

        if (key === 'Tab') {
          this.close();
          return;
        }

        if (key === 'ArrowDown') {
          event.preventDefault();
          this.moveActive(1);
          return;
        }

        if (key === 'ArrowUp') {
          event.preventDefault();
          this.moveActive(-1);
          return;
        }

        if (key === 'Home') {
          event.preventDefault();
          this.setActive(0);
          return;
        }

        if (key === 'End') {
          event.preventDefault();
          this.setActive(this.visibleOptions().length - 1);
          return;
        }

        if (key === 'Enter' || (key === ' ' && !this.search)) {
          event.preventDefault();
          const item = this.visibleOptions()[this.activeIndex];
          if (item) this.commit(item.dataset.value);
        }
      }

      onFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.close();
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.close();
      }
    }
  );
}
